import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Bot,
  Mic,
  MicOff,
  Send,
  X,
  Volume2,
  VolumeX,
  Sparkles,
  Loader2,
  StopCircle,
  Radio,
  Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHAT_TIMEOUT_MS = 45000;
const MAX_CHAT_RETRIES = 3;

// Sections that the AI should auto-narrate when Tour Mode is ON
const SECTION_META = {
  hero: { label: "Intro", prompt: "Give a 2 sentence warm intro about who Vedant is." },
  about: { label: "About", prompt: "Briefly explain what Vedant is about as a backend engineer." },
  system: { label: "System Design", prompt: "Explain the architecture diagram on screen in simple words." },
  projects: { label: "Projects", prompt: "Give a quick overview of Vedant's 5 featured projects." },
  skills: { label: "Skills", prompt: "Quickly summarise Vedant's main tech stack." },
  experience: { label: "Experience", prompt: "Summarise Vedant's internship experience." },
  github: { label: "GitHub", prompt: "Highlight the top GitHub repos visitors should check." },
  resume: { label: "Resume", prompt: "Summarise Vedant's education and achievements." },
  contact: { label: "Contact", prompt: "Tell the visitor how to reach Vedant." }
};

const SECTION_ORDER = Object.keys(SECTION_META);

const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [tourMode, setTourMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("vb_tour") === "1";
  });
  const [muted, setMuted] = useState(false);
  const [listening, setListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [text, setText] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hey! I'm Vedant's AI guide. Ask me anything about his projects, stack, or experience — in English, Hindi, or Hinglish!"
    }
  ]);
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined") return null;
    let sid = sessionStorage.getItem("vb_ai_sid");
    if (!sid) {
      sid =
        "s-" +
        (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
      sessionStorage.setItem("vb_ai_sid", sid);
    }
    return sid;
  });
  const [currentSection, setCurrentSection] = useState(null);

  const audioRef = useRef(null);
  const recognitionRef = useRef(null);
  const narratedSections = useRef(new Set());
  const messagesEndRef = useRef(null);
  const inFlightRef = useRef(false);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    sessionStorage.setItem("vb_tour", tourMode ? "1" : "0");
  }, [tourMode]);

  // Scroll chat to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // --- Audio playback helpers ---
  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (e) {}
    }
    setSpeaking(false);
  }, []);

  const speak = useCallback(
    async (text, language = "en") => {
      if (!text || muted) return;
      stopSpeaking();
      try {
        setSpeaking(true);
        console.log("Requesting TTS for:", text.slice(0, 50) + "...");
        const res = await axios.post(
          `${API}/ai/tts`,
          { text, language },
          { responseType: "blob", timeout: 60000 }
        );
        
        if (!res.data || res.data.size === 0) {
          throw new Error("Empty audio response from server");
        }

        const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "audio/mpeg" });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;

        audio.onended = () => {
          setSpeaking(false);
          URL.revokeObjectURL(url);
          audioRef.current = null;
        };

        audio.onerror = (err) => {
          console.error("Audio playback error:", err);
          setSpeaking(false);
          URL.revokeObjectURL(url);
          audioRef.current = null;
        };

        await audio.play();
      } catch (e) {
        console.error("TTS playback failed:", e);
        setSpeaking(false);
      }
    },
    [muted, stopSpeaking]
  );

  // --- Chat send ---
  const sendMessage = useCallback(
    async (userText, { silent = false, sectionContext } = {}) => {
      if (!userText?.trim() || inFlightRef.current) return;
      inFlightRef.current = true;
      setLoading(true);
      if (!silent) {
        setMessages((m) => [...m, { role: "user", content: userText }]);
      }
      try {
        let res;
        for (let attempt = 1; attempt <= MAX_CHAT_RETRIES; attempt += 1) {
          try {
            res = await axios.post(
              `${API}/ai/chat`,
              {
                session_id: sessionId,
                message: userText,
                section_context: sectionContext || currentSection,
                language: "auto"
              },
              { timeout: CHAT_TIMEOUT_MS }
            );
            break;
          } catch (error) {
            const status = error?.response?.status;
            const isRetriable =
              error?.code === "ECONNABORTED" ||
              error?.message === "Network Error" ||
              status >= 500;
            if (!isRetriable || attempt === MAX_CHAT_RETRIES) throw error;
            await sleep(1200 * attempt);
          }
        }
        const reply = res.data?.reply || "";
        const lang = res.data?.language_detected || "en";
        setMessages((m) => [...m, { role: "assistant", content: reply }]);
        if (res.data?.session_id) setSessionId(res.data.session_id);
        if (!muted) speak(reply, lang);
      } catch (e) {
        console.error("chat failed", e);
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Backend cold start ho sakta hai, 20-40 sec wait karke phir try karo."
          }
        ]);
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    },
    [sessionId, currentSection, muted, speak]
  );

  // --- Speech recognition (STT) ---
  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Speech recognition not supported in this browser. Please use Chrome.");
      return;
    }
    stopSpeaking();
    const rec = new SR();
    rec.lang = "en-IN";
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onstart = () => setListening(true);
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setListening(false);
      if (transcript) {
        setOpen(true);
        sendMessage(transcript);
      }
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
  }, [sendMessage, stopSpeaking]);

  const stopListening = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch (e) {}
    setListening(false);
  }, []);

  // --- IntersectionObserver for tour mode ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const id = visible.target.id;
          setCurrentSection(id);
        }
      },
      { threshold: [0.35, 0.6] }
    );
    SECTION_ORDER.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // When tour mode is ON and section changes, narrate it (only once per section)
  useEffect(() => {
    if (!tourMode || !currentSection || !sessionId) return;
    if (narratedSections.current.has(currentSection)) return;
    const meta = SECTION_META[currentSection];
    if (!meta) return;
    narratedSections.current.add(currentSection);
    // Fire silent prompt on the next tick so the effect stays side-effect safe.
    const timer = window.setTimeout(() => {
      setOpen(true);
      setMessages((m) => [
        ...m,
        { role: "assistant_tour_heading", content: `Tour · ${meta.label}` }
      ]);
      sendMessage(meta.prompt, { silent: true, sectionContext: currentSection });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => window.clearTimeout(timer);
  }, [currentSection, tourMode, sessionId, sendMessage]);

  // Toggle tour
  const toggleTour = () => {
    setTourMode((v) => {
      const next = !v;
      if (!next) {
        narratedSections.current.clear();
        stopSpeaking();
      }
      return next;
    });
  };

  const clearChat = async () => {
    stopSpeaking();
    setMessages([
      {
        role: "assistant",
        content:
          "Fresh slate! Ask me about Vedant's projects, stack, or experience."
      }
    ]);
    narratedSections.current.clear();
    try {
      await axios.delete(`${API}/ai/session/${sessionId}`);
    } catch (e) {}
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    sendMessage(text.trim());
    setText("");
  };

  // Quick starter prompts
  const starters = useMemo(
    () => [
      "Explain ResQride",
      "Why Kafka in Wallet?",
      "Is Vedant hireable?",
      "Tell me about ResQRide"
    ],
    []
  );

  return (
    <>
      {/* Floating action cluster */}
      <div className="fixed bottom-5 right-5 z-[80] flex flex-col items-end gap-3">
        {/* Listening indicator */}
        <AnimatePresence>
          {listening && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="px-4 h-10 rounded-full glass-strong flex items-center gap-2 glow-cyan"
            >
              <Radio className="w-3.5 h-3.5 text-cyan-300 animate-pulse" />
              <span className="font-mono text-[11px] tracking-wider text-cyan-200">
                listening…
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Speaking indicator */}
        <AnimatePresence>
          {speaking && !open && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              onClick={stopSpeaking}
              className="px-4 h-10 rounded-full glass-strong flex items-center gap-2 hover:border-cyan-400/40"
            >
              <StopCircle className="w-4 h-4 text-amber-300" />
              <span className="font-mono text-[11px] tracking-wider text-amber-200">
                stop voice
              </span>
            </motion.button>
          )}
        </AnimatePresence>

        {/* Tour mode toggle */}
        <button
          onClick={toggleTour}
          aria-label="Toggle AI tour mode"
          className={`px-4 h-10 rounded-full flex items-center gap-2 text-xs font-mono tracking-wider uppercase transition-colors border ${
            tourMode
              ? "bg-cyan-400 text-zinc-950 border-cyan-300 hover:bg-cyan-300"
              : "glass-strong text-zinc-300 hover:border-cyan-400/40 border-transparent"
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          {tourMode ? "Tour · on" : "AI Tour"}
        </button>

        {/* Main mic / chat button */}
        <div className="flex gap-3 items-end">
          {!open && (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
              onClick={listening ? stopListening : startListening}
              aria-label="Voice input"
              className={`w-12 h-12 rounded-full flex items-center justify-center border transition-colors ${
                listening
                  ? "bg-red-500 border-red-400 text-white animate-pulse"
                  : "glass-strong border-transparent text-cyan-300 hover:border-cyan-400/40"
              }`}
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => setOpen((v) => !v)}
            aria-label="Open AI assistant"
            className="relative w-14 h-14 rounded-full bg-cyan-400 hover:bg-cyan-300 text-zinc-950 flex items-center justify-center shadow-[0_10px_40px_-10px_rgba(34,211,238,0.6)] transition-colors"
          >
            {open ? <X className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
            {!open && (
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#050507]" />
            )}
          </motion.button>
        </div>
      </div>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-5 z-[79] w-[min(400px,94vw)] h-[min(560px,78vh)] rounded-2xl glass-strong overflow-hidden flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 h-14 px-4 border-b border-white/5 bg-[#0a0b10]">
              <div className="relative w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
                <Bot className="w-4 h-4 text-cyan-300" />
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0a0b10]" />
              </div>
              <div className="flex-1">
                <p className="font-display text-sm font-semibold text-white leading-none">
                  Vedant's AI Guide
                </p>
                <p className="font-mono text-[10px] tracking-wider text-zinc-500 mt-1">
                  {speaking ? "speaking…" : listening ? "listening…" : "online · gemini 2.5"}
                </p>
              </div>
              <button
                onClick={() => setMuted((v) => !v)}
                aria-label="Mute"
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                  muted
                    ? "bg-white/5 text-zinc-500"
                    : "bg-cyan-500/10 text-cyan-300"
                }`}
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button
                onClick={clearChat}
                aria-label="Clear"
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 flex items-center justify-center transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => {
                if (m.role === "assistant_tour_heading") {
                  return (
                    <div key={i} className="flex items-center gap-2 py-1">
                      <div className="flex-1 h-px bg-white/10" />
                      <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-cyan-300">
                        {m.content}
                      </span>
                      <div className="flex-1 h-px bg-white/10" />
                    </div>
                  );
                }
                const isUser = m.role === "user";
                return (
                  <div
                    key={i}
                    className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        isUser
                          ? "bg-cyan-400 text-zinc-950 rounded-br-sm"
                          : "bg-white/[0.06] border border-white/5 text-zinc-200 rounded-bl-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                );
              })}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/[0.06] border border-white/5 rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 text-cyan-300 animate-spin" />
                    <span className="font-mono text-xs text-zinc-400">thinking…</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Starters */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {starters.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] text-zinc-300 hover:text-cyan-200 hover:border-cyan-400/30 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={onSubmit}
              className="flex items-center gap-2 p-3 border-t border-white/5 bg-[#0a0b10]"
            >
              <button
                type="button"
                onClick={listening ? stopListening : startListening}
                aria-label="Mic"
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                  listening
                    ? "bg-red-500 text-white animate-pulse"
                    : "bg-white/5 text-cyan-300 hover:bg-white/10"
                }`}
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Ask in English, Hindi, or Hinglish..."
                className="flex-1 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40"
              />
              <button
                type="submit"
                disabled={!text.trim() || loading}
                className="w-10 h-10 rounded-xl bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-zinc-950 flex items-center justify-center transition-colors"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistant;
